import React, { useState } from 'react';
import { Check, Copy, Info } from 'lucide-react';

const serviceLabels = {
  clientes: 'Clientes',
  agendamientoPrevicar: 'Agendamiento Previcar',
  pagos: 'Pagos',
  bro: 'BRO',
  ViaRapida: 'BRO-ViaRapida'
};

const formatExpiration = (expiresIn) => {
  if (expiresIn === 'nunca') return 'Sin expiración';
  const value = parseInt(expiresIn);
  const unit = expiresIn.slice(-1);
  const units = {
    'h': 'horas',
    'd': 'días',
    'm': 'meses',
    'y': 'años'
  };
  return `${value} ${units[unit] || 'días'}`;
};

const formatPermissions = (permissions) => {
  const formattedPermissions = [];

  Object.entries(permissions).forEach(([service, operations]) => {
    const activeOperations = Object.entries(operations)
      .filter(([_, value]) => value)
      .map(([operation]) => operation.charAt(0).toUpperCase() + operation.slice(1));

    if (activeOperations.length > 0) {
      formattedPermissions.push({
        service: serviceLabels[service] || service,
        operations: activeOperations
      });
    }
  });

  return formattedPermissions;
};

const TokenDisplay = ({ token, onReset }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar el token:', err);
    }
  };

  if (!token) return null;

  const permissions = formatPermissions(token.permissions || {});

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Token Generado</h2>
          <button
            onClick={onReset}
            className="px-4 py-2 text-blue-600 font-medium hover:bg-blue-50 rounded-md transition-colors"
          >
            Generar Nuevo Token
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4 relative">
            <pre className="text-sm font-mono text-gray-700 whitespace-pre-wrap break-all">
              {token.token}
            </pre>
            <button
              onClick={handleCopy}
              className="absolute top-2 right-2 p-2 text-gray-500 hover:text-blue-600 bg-white rounded-md border border-gray-200 hover:border-blue-300 transition-colors"
              title="Copiar token"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
          {copied && (
            <p className="text-green-600 text-sm mt-2 flex items-center">
              <Check size={16} className="mr-1" /> Token copiado al portapapeles
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">Detalles</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-sm font-medium text-gray-500 w-24">Nombre:</span>
                <span className="text-sm text-gray-900">{token.name}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sm font-bold text-black w-24">ID:</span>
                <span className="text-sm text-gray-900">{token.id}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sm font-medium text-gray-500 w-24">Expiración:</span>
                <span className="text-sm text-gray-900">
                  {formatExpiration(token.expiresIn)}
                </span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">Permisos Concedidos</h3>
            {permissions.length > 0 ? (
              <ul className="space-y-3">
                {permissions.map(({ service, operations }) => (
                  <li key={service} className="text-sm">
                    <span className="font-medium text-gray-700">{service}</span>
                    <span className="text-gray-500">: {operations.join(', ')}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No se han concedido permisos.</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <Info className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Instrucciones de Uso</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ol className="list-decimal pl-5 space-y-1">
                <li>Ir al módulo <span className='rounded-md p-1 font-bold bg-blue-500 text-white'>GESTION DE USUARIOS</span>.</li>
                <li>Crear o editar un usuario.</li>
                <li>
                  Asignarle al usuario el <b>ID</b> del token generado. <br/>
                  <b>NOTA:</b> El <b>ID</b> del token se indica en <i className='font-bold'>Detalles ↑</i> o en el módulo <span className='rounded-md p-1 font-bold bg-blue-500 text-white'>VER TOKENS</span>.
                </li>
                <li>
                  El usuario debe iniciar sesión:
                  <ol className="list-decimal pl-5 mt-1 space-y-1">
                    <li>Si es administrador, desde el panel administrador hacer clic en el botón <span className='rounded-md p-1 font-bold bg-yellow-400 text-black'>Ir a Interlink</span>.</li>
                    <li>Si no es administrador, se redireccionará automáticamente a la documentación de la API.</li>
                  </ol>
                </li>
                <li>Solo se mostrarán los endpoints a los que tiene permiso de acceso.</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenDisplay; 